import { useRef, useState } from 'react'
import { Upload } from 'lucide-react'

import { cn } from '@/lib/utils'

export interface FileDropzoneProps {
  /** The file input's `accept`, e.g. "image/*". */
  accept: string
  onFiles: (files: File[]) => void
  title: string
  hint?: string
  multiple?: boolean
  /** A slimmer strip, for adding files under a list that is already there. */
  compact?: boolean
  disabled?: boolean
}

/** A click-or-drop target for picking one or more files. */
export function FileDropzone({
  accept,
  onFiles,
  title,
  hint,
  multiple = true,
  compact,
  disabled,
}: FileDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)

  const take = (list: FileList | null) => {
    const files = Array.from(list ?? [])
    if (files.length) onFiles(multiple ? files : files.slice(0, 1))
  }

  return (
    <>
      <button
        type="button"
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault()
          if (!disabled) setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault()
          setDragging(false)
          if (!disabled) take(e.dataTransfer.files)
        }}
        className={cn(
          'flex w-full cursor-pointer flex-col items-center justify-center gap-1.5 rounded-lg border-2 border-dashed border-border text-center transition-colors',
          'hover:border-primary/50 focus-visible:border-primary focus-visible:outline-none disabled:cursor-default disabled:opacity-60',
          compact ? 'px-4 py-3' : 'px-6 py-12',
          dragging && 'border-primary bg-primary/5'
        )}
      >
        <Upload className={cn('text-muted-foreground', compact ? 'size-4' : 'size-6')} />
        <span className="text-sm font-medium">{title}</span>
        {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
      </button>
      <input
        ref={inputRef}
        type="file"
        className="sr-only"
        tabIndex={-1}
        accept={accept}
        multiple={multiple}
        onChange={(e) => {
          take(e.target.files)
          e.target.value = ''
        }}
      />
    </>
  )
}
